import { filterClasses } from "@swivl-mono/helpers";
import { SessionInterface } from "@swivl-mono/types";
import { Button, ButtonColor, Spinner } from "@swivl-mono/ui";
import { type FormEvent, type RefObject } from "react";
import { MdAddComment, MdOutlineAddComment } from "react-icons/md";
import { PiLightning, PiLightningFill } from "react-icons/pi";

import { Tooltip } from "../../../../../../Components/Tooltip/Tooltip";
import { type ChannelColor } from "../../../../../../Helpers/Conversations/Conversations.Helper";
import useHasPendingUpdates from "../../../../../../Hooks/useHasPendingUpdates";
import { PendingUpdateType } from "../../../../../../Models/Pending/Pending.Model";
import { UsersModel } from "../../../../../../Models/Users/Users.Model";

interface ChatFormProps {
  currentChannelColor: ChannelColor;
  sendMessage: (message: string) => void;
  isQuickRepliesDropdownOpen: boolean;
  isAutoCompleteEnabled: boolean;
  isAutoCompleteLoading: boolean;
  hasAutoCompleteText: boolean;
  toggleAutoCompleteEnabled: () => void;
  handleToggleQuickRepliesDropdown: (isOpen?: boolean) => void;
  handleSetText: (text: string) => void;
  textAreaRef: RefObject<HTMLTextAreaElement>;
  text: string;
  session: SessionInterface;
  agentAutoCompleteText?: string;
}

const BaseChatForm = ({
  currentChannelColor,
  isQuickRepliesDropdownOpen,
  isAutoCompleteLoading,
  handleToggleQuickRepliesDropdown,
  isAutoCompleteEnabled,
  hasAutoCompleteText,
  toggleAutoCompleteEnabled,
  handleSetText,
  sendMessage,
  text,
  textAreaRef,
  session,
  agentAutoCompleteText
}: ChatFormProps) => {
  const handleEndTakeover = () => {
    const sessionEscalationId = session.escalationId || session.escalation?.id;
    if (sessionEscalationId) {
      UsersModel.endTakeoverOfSession(session.id, sessionEscalationId);
    }
  };
  const isPending = useHasPendingUpdates([{ type: PendingUpdateType.SESSION_ESCALATION, id: session?.id }]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const message = formData.get("channelMessage") as string;

    if (message.trim()) {
      sendMessage(message);
      handleSetText("");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="size-full max-h-[144px]">
      <textarea
        className={`${currentChannelColor.border} h-[84px] w-full resize-none rounded-[3px] border border-grey-400 p-3 transition-colors duration-200 ease-in-out`}
        name="channelMessage"
        placeholder={hasAutoCompleteText ? agentAutoCompleteText : "Enter your message..."}
        id="channelMessage"
        value={text}
        onChange={(e) => handleSetText(e.target.value)}
        ref={textAreaRef}
      />
      <div className="mt-1 flex items-center justify-between">
        <Button color={ButtonColor.WHITE} onClick={handleEndTakeover} loading={isPending} type="button">
          End Takeover
        </Button>

        <div className="flex items-center">
          <Tooltip
            classNames={{ tooltip: "text-xs" }}
            delay={{ open: 1000 }}
            content={isAutoCompleteEnabled ? "Disable Autocomplete" : "Enable Autocomplete"}
          >
            <button
              className={filterClasses(
                "flex size-[30px] items-center justify-center transition-colors duration-200 hover:text-orange-500",
                isAutoCompleteEnabled ? "text-teal-500" : "text-black"
              )}
              onClick={toggleAutoCompleteEnabled}
              type="button"
            >
              {isAutoCompleteEnabled ? <PiLightningFill className="size-6" /> : <PiLightning className="size-6" />}
            </button>
          </Tooltip>

          <div className="centerInfoText">
            {isAutoCompleteEnabled && isAutoCompleteLoading ? <Spinner /> : hasAutoCompleteText ? "Press Tab to include suggestion" : ""}
          </div>

          <Tooltip
            classNames={{ tooltip: "text-xs" }}
            delay={{ open: 1000 }}
            content={isQuickRepliesDropdownOpen ? "Close Quick Replies" : "Open Quick Replies"}
          >
            <button
              className={filterClasses(
                "transitions-colors mr-2 flex size-[30px] items-center justify-center rounded-md duration-200 hover:text-orange-500",
                isQuickRepliesDropdownOpen ? "text-orange-500" : "text-inherit"
              )}
              onClick={() => handleToggleQuickRepliesDropdown()}
              type="button"
            >
              {isQuickRepliesDropdownOpen ? <MdAddComment className="size-6" /> : <MdOutlineAddComment className="size-6" />}
            </button>
          </Tooltip>

          <Button color={currentChannelColor.buttonColor} type="submit">
            Send
          </Button>
        </div>
      </div>
    </form>
  );
};

export const ChatForm = BaseChatForm;
